import { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import Layout from "../utils/Layout";
import AlertBar from "../utils/AlertBar";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config/environment";
import { useTranslation } from "react-i18next";
import { useTitle } from "../utils/useTitle";
import { useAnalyticsEventTracker } from "../utils/useAnalyticsEventTracker";

const PasswordReset = () => {
  useTitle("Password Reset");
  const eventTracker = useAnalyticsEventTracker();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const { token } = useParams();

  const [formData, setFormData] = useState({
    password: "",
    passwordConfirmation: "",
  });
  const [errors, setErrors] = useState({
    password: false,
    passwordConfirmation: false,
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [resetSucceed, setResetSucceed] = useState(false);

  function handleIsSuccessState() {
    setIsSuccess(false);
  }

  const handleInputChange = (e) => {
    if (!token) {
      setErrors({ error: true });
      setErrorMessage(t("auth.alertMsg.error5"));
      return;
    }

    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear the error for the current field when the user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);
    // Validate form fields
    const formErrors = {};
    Object?.keys(formData).forEach((key) => {
      if (!formData[key]) {
        formErrors[key] = true;
      }
    });

    if (Object?.keys(formErrors).length > 0) {
      // If there are errors, set the state to show errors
      setErrors(formErrors);
      setErrorMessage(t("auth.alertMsg.error1"));
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.passwordConfirmation) {
      setErrors({ error: true });
      setErrorMessage(t("auth.alertMsg.error2"));
      setIsLoading(false);
      return;
    }

    // Placeholder for the API request logic
    const url = `${API_URL}/auth/resetPassword/${token}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      console.log(data);

      if (data.status === "success") {
        setIsSuccess(true);
        setSuccessMessage(t("auth.alertMsg.success2"));
        setResetSucceed(true);
        eventTracker("password_reset");
      } else {
        setErrors({ error: true });
        setErrorMessage(data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      setErrors({ error: true });
      setErrorMessage(t("auth.alertMsg.error3"));
    } finally {
      // Reset form data and errors after successful submission or error
      setFormData({
        password: "",
        passwordConfirmation: "",
      });
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <Grid container>
        {/* Left Side (8 columns) */}
        <Grid item xs={5} container justify="center" alignItems="center">
          <Grid
            container
            direction="column"
            spacing={3}
            style={{ maxWidth: "800px" }}
          >
            <Grid item>
              <h1>{t("auth.header.resetPassword")}</h1>
              <Typography variant="h5" gutterBottom>
                {resetSucceed
                  ? t("auth.msg.resetSuccess")
                  : t("auth.msg.resetFailed")}
              </Typography>
            </Grid>
            {resetSucceed ? (
              ""
            ) : (
              <>
                <Grid item>
                  <TextField
                    label={t("auth.password")}
                    type="password"
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    name="password"
                    error={errors.password}
                    helperText={
                      errors.password && t("auth.requiredMsg.password")
                    }
                  />
                </Grid>
                <Grid item>
                  <TextField
                    label={t("auth.passwordConfirmation")}
                    type="password"
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.passwordConfirmation}
                    onChange={handleInputChange}
                    name="passwordConfirmation"
                    error={errors.passwordConfirmation}
                    helperText={
                      errors.passwordConfirmation &&
                      t("auth.requiredMsg.passwordConfirmation")
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item>
              {resetSucceed ? (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  size="large"
                  style={{ height: "55px" }}
                  onClick={() => {
                    navigate("/signin");
                  }}
                >
                  {t("auth.btnText.resetSuccess")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  size="large"
                  style={{ height: "55px" }}
                  onClick={handleFormSubmit}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("auth.btnText.resetFailed")
                  )}
                </Button>
              )}
            </Grid>

            <Grid item></Grid>
          </Grid>
        </Grid>

        {/* Right Side (6 columns) */}
        <Grid item xs={6} style={{ overflow: "hidden" }}>
          <div style={{ width: "100%", height: "100vh" }}>
            <img
              src="assets/images/banner1.jpg"
              alt="Password-Reset"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        </Grid>
      </Grid>

      <AlertBar
        open={Object.values(errors).some((error) => error)}
        message={errorMessage}
        severity={"error"}
        onClose={() => setErrors({})}
      />

      <AlertBar
        open={isSuccess}
        message={successMessage}
        severity={"success"}
        onClose={handleIsSuccessState}
      />
    </Layout>
  );
};

export default PasswordReset;
